import { useEffect, useState } from 'react';
import { useT } from '../i18n/i18n';
import { loadMedia, saveImage, shrinkImage } from '../media/media';
import { wordsOf } from '../games/kinds/PictureSentence';
import Pic from '../media/Pic';

type Props = {
  label: string;
  hint?: string;
  pictureLabel: string;
  sentencesLabel: string;
  picture: string;
  sentences: string[];
  maxSentences: number;
  maxWords: number;
  onChange: (next: { picture?: string; sentences?: string[] }) => void;
};

export default function PictureSentenceEditor({ label, hint, pictureLabel, sentencesLabel, picture, sentences, maxSentences, maxWords, onChange }: Props) {
  const t = useT();
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { void loadMedia(null); }, []);

  async function choose(file: File | undefined) {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const term = picture && !picture.startsWith('/') ? picture : `picture-${crypto.randomUUID().slice(0, 8)}`;
      await saveImage(term, await shrinkImage(file, 1400), null);
      onChange({ picture: term });
    } catch (e) {
      setError(e instanceof Error ? e.message : t('That picture could not be saved.'));
    } finally {
      setBusy(false);
    }
  }

  function add() {
    const s = draft.trim().replace(/\s+/g, ' ');
    const n = wordsOf(s).length;
    if (n < 2) { setError(t('A sentence needs at least 2 words.')); return; }
    if (s.split(' ').length > maxWords) { setError(t('A sentence can have at most {n} words.', { n: maxWords })); return; }
    setError(null);
    onChange({ sentences: [...sentences, s] });
    setDraft('');
  }

  return (
    <fieldset className="sub-group picsent-editor">
      <legend className="field-label">{t(label)}</legend>
      {hint && <p className="field-hint" style={{ marginTop: 0 }}>{t(hint)}</p>}

      <div className="field">
        <span className="field-label">{t(pictureLabel)}</span>
        {picture ? <EditorPicture picture={picture} /> : <p className="muted">{t('No picture yet.')}</p>}
        <label className="btn secondary picsent-upload">
          <input type="file" accept="image/*" className="visually-hidden" disabled={busy} onChange={(e) => { void choose(e.target.files?.[0]); e.target.value = ''; }} />
          {busy ? t('Saving…') : picture ? t('Choose another picture') : t('Choose a picture')}
        </label>
      </div>

      <div className="field">
        <span className="field-label">{t(sentencesLabel)}</span>
        {sentences.length > 0 && (
          <ol className="picsent-list">
            {sentences.map((s, i) => (
              <li key={`${i}-${s}`}>
                <span>{s} <span className="muted">· {t('{n} words', { n: wordsOf(s).length })}</span></span>
                <button type="button" className="btn quiet-danger" onClick={() => onChange({ sentences: sentences.filter((_, j) => j !== i) })}>{t('Remove')}</button>
              </li>
            ))}
          </ol>
        )}
        {sentences.length < maxSentences && (
          <div className="picsent-add">
            <input aria-label={t('New sentence')} value={draft} maxLength={200} placeholder={t('e.g. A white boat floats on the blue sea.')}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} />
            <button type="button" className="btn secondary" disabled={!draft.trim()} onClick={add}>{t('Add sentence')}</button>
          </div>
        )}
        <p className="field-hint">{t('Up to {n} words in each sentence. Write it the way it should be built, with capitals and full stops if you want them.', { n: maxWords })}</p>
      </div>
      {error && <p className="error" role="alert">{error}</p>}
    </fieldset>
  );
}

function EditorPicture({ picture }: { picture: string }) {
  if (picture.startsWith('/')) return <img className="picsent-preview" src={picture} alt="" draggable={false} />;
  return <Pic text={picture} className="picsent-preview" fallback={<div className="picsent-preview g-scene-missing">Picture not found</div>} />;
}
